import { useCallback, useRef, useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import { useMeta } from "@/hooks";
import { MUTATION_DELETE_CUSTOMER_DATA } from "../../../apollo/module-operations/ccp";

const useCcpaDeleteCustomer = ({ onCompleted, onCloseSuccess } = {}) => {
    const { user } = useMeta();
    const [step, setStep] = useState("closed");
    const [customer, setCustomer] = useState(null);
    const [result, setResult] = useState(null);
    const stepRef = useRef(step);
    const resultRef = useRef(result);
    stepRef.current = step;
    resultRef.current = result;

    const [deleteCustomerData] = useMutation(MUTATION_DELETE_CUSTOMER_DATA, {
        onCompleted: data => {
            const nextResult = data?.deleteCustomerData ?? {
                success: false,
                message: "Failed to delete customer data",
                deleted_resources: [],
            };

            setResult(nextResult);
            setStep("result");
            onCompleted?.(nextResult);
        },
        onError: error => {
            const nextResult = {
                success: false,
                message: error?.message || "Error deleting customer data",
                deleted_resources: [],
            };

            setResult(nextResult);
            setStep("result");
            onCompleted?.(nextResult);
        },
    });

    const openDelete = useCallback(nextCustomer => {
        setCustomer(nextCustomer);
        setResult(null);
        setStep("confirm");
    }, []);

    const close = useCallback(() => {
        if (stepRef.current === "loading") {
            return;
        }

        const closedAfterSuccess =
            stepRef.current === "result" && resultRef.current?.success;

        // Keep the overlay up until this click finishes, otherwise the
        // same pointer event hits the customers table row and opens detail.
        window.setTimeout(() => {
            setStep("closed");
            setCustomer(null);
            setResult(null);
            if (closedAfterSuccess) {
                onCloseSuccess?.();
            }
        }, 50);
    }, [onCloseSuccess]);

    const confirm = useCallback(() => {
        if (!customer?.id) {
            return;
        }

        setStep("loading");
        deleteCustomerData({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: {
                    customer_id: String(customer.id).trim(),
                },
            },
        });
    }, [customer, deleteCustomerData, user.accountId, user.storeId]);

    return {
        openDelete,
        modalProps: {
            open: step !== "closed",
            step,
            customer,
            result,
            onConfirm: confirm,
            onClose: close,
        },
    };
};

export default useCcpaDeleteCustomer;
